import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Keeps the custom price block in `snippets/variant-main-picker.liquid` in sync
 * when variant changes (main picker or sticky selector).
 *
 * Uses HTML from the variant section response so amounts match Liquid `| money`
 * (presentment currency, subunits, B2B/catalog pricing) — avoids client-side scaling bugs.
 *
 * @extends {Component<Record<string, never>>}
 */
class VariantMainPriceSync extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener('purchase-mode:change', this.#applyCatalogPricesFromDataset, { signal });
    this.#applyCatalogPricesFromDataset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #applyCatalogPricesFromDataset = () => {
    const formattedCurrent = (this.dataset.catalogPriceFormatted || '').trim();
    const formattedCompare = (this.dataset.catalogCompareFormatted || '').trim();

    const currentEl = this.querySelector('.price--current');
    if (currentEl instanceof HTMLElement && formattedCurrent) {
      currentEl.textContent = formattedCurrent;
    }

    const compareEl = this.querySelector('.price--compare');
    if (compareEl instanceof HTMLElement) {
      if (formattedCompare) {
        compareEl.textContent = formattedCompare;
        compareEl.hidden = false;
      }
    }
  };

  /**
   * @param {import('./events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const productId = this.dataset.productId;
    const eventProductId = event.detail.data.productId?.toString();
    if (!productId || eventProductId !== productId.toString()) return;

    const doc = event.detail.data.html;
    if (!doc || typeof doc.querySelector !== 'function') return;

    const selector = `variant-main-price-sync[data-product-id="${CSS.escape(productId)}"]`;
    const newBlock = doc.querySelector(selector);
    if (newBlock instanceof HTMLElement) {
      this.dataset.priceUpdating = 'true';
      morph(this, newBlock, MORPH_OPTIONS);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#applyCatalogPricesFromDataset();
          this.dataset.priceUpdating = 'false';
        });
      });
    }
  };
}

if (!customElements.get('variant-main-price-sync')) {
  customElements.define('variant-main-price-sync', VariantMainPriceSync);
}
